import { z } from 'zod';
import { NotificationCategory } from '../enums.js';
import { isoDatetime, paginationQuery, uuid } from './common.js';

// NotificationCategory is canonical in shared/src/enums.ts. We just build
// the Zod enum here from those values so list queries can validate the
// `category` query param.
const notificationCategoryEnum = z.enum([
  NotificationCategory.ANNOUNCEMENT,
  NotificationCategory.PAYMENT,
  NotificationCategory.BOOKING,
  NotificationCategory.COMPLAINT,
  NotificationCategory.VISITOR,
  NotificationCategory.PACKAGE,
  NotificationCategory.EMERGENCY,
  NotificationCategory.SYSTEM,
]);

// ─── Public shape returned by GET endpoints ────────────────────────────────
export const publicNotification = z.object({
  id: uuid,
  category: notificationCategoryEnum,
  title: z.string(),
  body: z.string(),
  // Deep link (e.g. /announcements/abc123, /invoices/xyz) tap-through target.
  // Null when the notification is purely informational.
  deep_link: z.string().nullable(),
  // Extra context bag the feature module set when sending (e.g. invoice_id).
  // Free-form by design.
  payload: z.record(z.unknown()).nullable(),
  read_at: isoDatetime.nullable(),
  created_at: isoDatetime,
});
export type PublicNotification = z.infer<typeof publicNotification>;

// ─── Resident: list query ──────────────────────────────────────────────────
// `days` controls the time window. Default 14 (Facebook-style two-week
// inbox); max 60 to keep the query bounded.
export const listNotificationsQuery = paginationQuery.extend({
  days: z.coerce.number().int().min(1).max(60).default(14),
  category: notificationCategoryEnum.optional(),
  unread_only: z.coerce.boolean().optional(),
});
export type ListNotificationsQuery = z.infer<typeof listNotificationsQuery>;

export const listNotificationsResponse = z.object({
  items: z.array(publicNotification),
  next_cursor: z.string().nullable(),
  // Total unread count across the 14d window — drives the bottom-nav
  // badge so the count survives across pages of paginated content.
  unread_count: z.number().int().nonnegative(),
});
export type ListNotificationsResponse = z.infer<typeof listNotificationsResponse>;

// ─── Lightweight unread-count endpoint ─────────────────────────────────────
// Cheap COUNT() query the bottom-nav badge polls without paying for the
// full list payload.
export const unreadCountResponse = z.object({
  unread_count: z.number().int().nonnegative(),
});
export type UnreadCountResponse = z.infer<typeof unreadCountResponse>;

// ─── Mark-read responses ───────────────────────────────────────────────────
export const markReadResponse = z.object({
  ok: z.literal(true),
  notification: publicNotification.optional(),
});
export type MarkReadResponse = z.infer<typeof markReadResponse>;

export const markAllReadResponse = z.object({
  ok: z.literal(true),
  marked: z.number().int().nonnegative(),
});
export type MarkAllReadResponse = z.infer<typeof markAllReadResponse>;
